import { CaseFilterQueryParam, LensHomeFeatures } from "redux/slices/clientLens";
import { CaseBrowserTab } from "routes/lenses/lens/case/CaseExports";
import { ClusterType, SectionReviewState, SectionValueMatchMode } from "generated/graphql";

const lensDetailBuilder = (lensId: number): string => {
  return `/lens/${lensId}`;
};

const lensEditorDetailBuilder = (lensId?: number): string => {
  return lensId ? `/lens-editor/${lensId}` : "/lens-editor";
};

const aiTests = (lensId: number) => `${lensEditorDetailBuilder(lensId)}/ai/tests`;
const aiTestsField = (lensId: number, fieldId: number) => `${aiTests(lensId)}/${fieldId}`;

export const AppRoutes = {
  signinExpired: "/signin?expired=true",
  lensDetailBuilder,
  lensEditorDetailBuilder,
  lensHomeBuilder: (
    lensId: number,
    feature?: LensHomeFeatures,
    tab?: "reviewer" | "admin",
    params?: string
  ): string => {
    const searchParams = new URLSearchParams(params);
    if (tab) searchParams.set("tab", tab);
    const paramsString = searchParams.toString();
    const featureSegment = feature === "cases" ? "/cases" : feature === "export-config" ? "/export-config" : "";
    const paramsSegment = paramsString ? `?${paramsString}` : "";
    return `/lens/${lensId}${featureSegment}${paramsSegment}`;
  },
  lensFilesBuilder: (lensId: number, feature?: "documents" | "exports"): string => {
    return `${lensDetailBuilder(lensId)}/files${feature ? `/${feature}` : ""}`;
  },
  lensReportsBuilder: (lensId: number, reportId?: string): string => {
    return `${lensDetailBuilder(lensId)}/report${reportId ? `/${reportId}` : ""}`;
  },
  lensCaseImportBuilder: (lensId: number): string => {
    return `${lensDetailBuilder(lensId)}/case-import`;
  },
  lensExportConfigBuilder: (lensId: number, configId?: number): string => {
    return `${lensDetailBuilder(lensId)}/export-config${configId ? `/${configId}` : ""}`;
  },
  caseDetailBuilder: (lensId: number, caseId: number, tab?: CaseBrowserTab): string => {
    return `${lensDetailBuilder(lensId)}/cases/${caseId}` + (tab ? `/${tab}` : "");
  },
  caseDetailReviewRule: (lensId: number, caseId: number, ruleIds: string[]): string => {
    const rulesEncoded = ruleIds.join(",");
    const params = "?rules=" + rulesEncoded + "&review_type=grouped";

    return AppRoutes.caseDetailBuilder(lensId, caseId, "review") + params;
  },
  aiChatBuilder: (lensId: number, chatId: string): string => {
    return `/lens-editor/${lensId}/ai/chats/${chatId}`;
  },
  caseDetailReviewField: (
    lensId: number,
    caseId: number,
    fieldId: number,
    familyOnly?: boolean,
    hideNegated?: boolean
  ): string => {
    const params = new URLSearchParams();
    params.set("field_id", fieldId.toString());
    if (familyOnly) {
      params.set("records", ClusterType.FamilyHistory);
    }
    if (hideNegated) {
      params.set("hide_negated", "true");
    }
    params.set("review_type", "grouped");
    return AppRoutes.caseDetailBuilder(lensId, caseId, "review") + "?" + params;
  },
  discoverLink: (lensId: number, caseId: number, query?: string): string => {
    return AppRoutes.caseDetailBuilder(lensId, caseId, "review") + "?mode=discover&review_type=chrono&query=" + query;
  },
  supplementalDataURLBuilder: (lensId: number, caseId: number, reportId: string): string => {
    return `/lens/${lensId}/report/${reportId}?caseId=${caseId}`;
  },
  caseDocumentsBuilder: (options: {
    lensId: number;
    caseId: number;
    documentId?: number | string;
    page?: number;
  }): string => {
    const { lensId, caseId, documentId, page } = options;

    const params = new URLSearchParams();

    const docId = documentId !== undefined ? documentId : "";

    if (page !== undefined) {
      params.set("page", `${page}`);
    }

    return `${lensDetailBuilder(lensId)}/cases/${caseId}/documents/${docId}?${params}`;
  },
  caseProfileSection: ({
    lensId,
    caseId,
    formPageId,
    sectionId,
    fieldId,
    valueGroupId,
    referenceId,
    page,
  }: {
    lensId: number;
    caseId: number;
    formPageId?: number;
    sectionId?: number;
    fieldId?: number;
    valueGroupId?: number;
    referenceId?: number;
    page?: number;
  }): string => {
    const params = new URLSearchParams();

    if (formPageId !== undefined) {
      params.set("formPageId", formPageId.toString());
    }
    if (sectionId !== undefined) {
      params.set("sectionId", sectionId.toString());
    }
    if (fieldId !== undefined) {
      params.set("fieldId", fieldId.toString());
    }
    if (valueGroupId !== undefined) {
      params.set("valueGroupId", valueGroupId.toString());
    }
    if (referenceId !== undefined) {
      params.set("referenceId", referenceId.toString());
    }
    if (page !== undefined) {
      params.set("page", page.toString());
    }

    return `/lens/${lensId}/cases/${caseId}/profile?${params}`;
  },
  caseTable: ({
    lensId,
    caseStatus,
    reviewStatus,
    reviewStatusReason,
    search,
    assigneeId,
    firmId,
    page,
    rowsPerPage,
    hasDocuments,
    sectionStatuses,
    sectionIds,
    sectionAssignees,
    sectionMatchMode,
  }: {
    lensId: number;
    caseStatus?: string;
    reviewStatus?: string;
    reviewStatusReason?: string;
    search?: string;
    assigneeId?: number;
    firmId?: number;
    page?: number;
    rowsPerPage?: number;
    hasDocuments?: boolean;
    sectionStatuses?: SectionReviewState[];
    sectionIds?: number[];
    sectionAssignees?: number[];
    sectionMatchMode?: SectionValueMatchMode;
  }) => {
    const params = new URLSearchParams();

    if (caseStatus !== undefined) params.set(CaseFilterQueryParam.CASE_STATUS, caseStatus);
    if (reviewStatus !== undefined) params.set(CaseFilterQueryParam.REVIEW_STATUS, reviewStatus);
    if (reviewStatusReason !== undefined) params.set(CaseFilterQueryParam.REVIEW_STATUS_REASON, reviewStatusReason);
    if (search !== undefined) params.set(CaseFilterQueryParam.SEARCH, search);
    if (assigneeId !== undefined) params.set(CaseFilterQueryParam.ASSIGNEE_ID, assigneeId.toString());
    if (firmId !== undefined) params.set(CaseFilterQueryParam.FIRM_ID, firmId.toString());
    if (page !== undefined) params.set(CaseFilterQueryParam.PAGE, page.toString());
    if (rowsPerPage !== undefined) params.set(CaseFilterQueryParam.ROWS_PER_PAGE, rowsPerPage.toString());
    if (hasDocuments !== undefined) params.set(CaseFilterQueryParam.HAS_DOCUMENTS, hasDocuments.toString());
    if (sectionStatuses !== undefined)
      sectionStatuses.forEach((status) => params.append(CaseFilterQueryParam.SECTION_STATUS, status));
    if (sectionIds !== undefined)
      sectionIds.forEach((sectionId) => params.append(CaseFilterQueryParam.SECTION_ID, sectionId.toString()));
    if (sectionMatchMode !== undefined) params.set(CaseFilterQueryParam.SECTION_MATCH_MODE, sectionMatchMode);

    if (sectionAssignees !== undefined)
      sectionAssignees.forEach((assignee) =>
        params.append(CaseFilterQueryParam.SECTION_ASSIGNEES, assignee.toString())
      );

    return `/lens/${lensId}/cases?${params}`;
  },
  usersAppInviteBuilder: (app: "collect" | "pattern") => {
    return `/users/${app}/invite`;
  },
  collectUsers: () => {
    return "/users/collect";
  },
  patternUsers: () => {
    return "/users/pattern";
  },
  lensEditorCasePropertiesGroups: (lensId: number) => `${lensEditorDetailBuilder(lensId)}/case-properties/groups`,
  lensEditorCasePropertiesGroup: (lensId: number, groupName: string) =>
    `${lensEditorDetailBuilder(lensId)}/case-properties/groups/${groupName}`,
  aiTests,
  aiTestsField,
  aiTestsFieldRun: (lensId: number, fieldId: number) => `${aiTestsField(lensId, fieldId)}/run`,
};
